import React, { useMemo } from 'react';
import StatisticsGlassCard from './StatisticsGlassCard';
import StatisticsDesktopSectionHeader from './StatisticsDesktopSectionHeader';
import { STATS_ICONS } from './statisticsAssets';

export default function StatisticsDesktopVisitationHeatmapCard({
    visitationHeatmapStats,
    isEditMode,
    longPressHandlers,
    theme
}) {
    const primaryColorHex = theme?.primaryHex || '#6366F1';

    const weeks = useMemo(() => {
        if (!visitationHeatmapStats || visitationHeatmapStats.length === 0) return [];

        const groups = {};
        visitationHeatmapStats.forEach(cell => {
            if (!groups[cell.weekIndex]) groups[cell.weekIndex] = [];
            groups[cell.weekIndex].push(cell);
        });

        return Object.keys(groups)
            .sort((a, b) => Number(a) - Number(b))
            .map(k => ({
                weekIndex: k,
                days: groups[k].sort((a, b) => a.dayIndex - b.dayIndex),
            }));
    }, [visitationHeatmapStats]);

    const maxCount = useMemo(() => {
        if (!visitationHeatmapStats?.length) return 1;
        return Math.max(1, ...visitationHeatmapStats.map(c => c.count));
    }, [visitationHeatmapStats]);

    const getColor = (count) => {
        if (count === 0) return '#F3F4F6';
        const ratio = count / maxCount;
        const opacity = 0.25 + (ratio * 0.75);
        return primaryColorHex;
    };

    const getOpacity = (count) => {
        if (count === 0) return 1;
        return 0.25 + ((count / maxCount) * 0.75);
    };

    const totalVisits = useMemo(() => {
        return visitationHeatmapStats?.reduce((acc, curr) => acc + curr.count, 0) || 0;
    }, [visitationHeatmapStats]);

    const dayLabels = ['일', '', '화', '', '목', '', '토'];

    return (
        <StatisticsGlassCard className="h-full p-6 flex flex-col" {...longPressHandlers} isEditMode={isEditMode}>
            <div className="w-full mb-4">
                <StatisticsDesktopSectionHeader
                    iconSrc={STATS_ICONS.activity || STATS_ICONS.target}
                    title="심방 활동 빈도"
                    badge={`${totalVisits}건`}
                />
            </div>

            <div className="flex-1 w-full flex items-center justify-center overflow-hidden">
                {weeks.length > 0 ? (
                    <div className="flex flex-col gap-1.5 w-full">
                        <div className="flex gap-[3px]">
                            {/* Day labels */}
                            <div className="flex flex-col justify-around w-5 shrink-0">
                                {dayLabels.map((label, i) => (
                                    <span key={i} className="text-[9px] font-bold text-gray-400 h-[14px] flex items-center">{label}</span>
                                ))}
                            </div>

                            {/* Grid */}
                            <div className="flex-1 flex gap-[3px]">
                                {weeks.map((weekData) => (
                                    <div key={`week-${weekData.weekIndex}`} className="flex flex-col gap-[3px] flex-1">
                                        {weekData.days.map((cell) => (
                                            <div
                                                key={`${cell.weekIndex}-${cell.dayIndex}`}
                                                className="w-full aspect-square rounded-[3px] transition-all duration-300 hover:scale-125 hover:z-10 relative cursor-default"
                                                style={{
                                                    backgroundColor: getColor(cell.count),
                                                    opacity: getOpacity(cell.count),
                                                }}
                                                title={`${cell.date}: ${cell.count}건`}
                                            />
                                        ))}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Legend */}
                        <div className="flex items-center justify-end gap-1.5 mt-2 text-[10px] font-medium text-gray-400">
                            <span>적음</span>
                            <div className="w-3 h-3 rounded-[2px]" style={{ backgroundColor: '#F3F4F6' }} />
                            <div className="w-3 h-3 rounded-[2px]" style={{ backgroundColor: primaryColorHex, opacity: 0.3 }} />
                            <div className="w-3 h-3 rounded-[2px]" style={{ backgroundColor: primaryColorHex, opacity: 0.6 }} />
                            <div className="w-3 h-3 rounded-[2px]" style={{ backgroundColor: primaryColorHex, opacity: 1 }} />
                            <span>많음</span>
                        </div>
                    </div>
                ) : (
                    <div className="text-gray-300 text-sm font-bold text-center">
                        <div className="text-3xl mb-2">🗓️</div>
                        <p>활동 데이터가 없습니다</p>
                    </div>
                )}
            </div>
        </StatisticsGlassCard>
    );
}
